<script lang="ts" setup>
import { getGeopoint } from '@thecompaniesapi/company-helpers'
import Draggable from 'vuedraggable'

defineProps({
  bordered: {
    default: false,
    type: Boolean,
  },
  company: {
    required: true,
    type: Object as PropType<CompanyV2>,
  },
  similar: {
    default: () => [],
    type: Array as PropType<CompanyV2[]>,
  },
})

const emit = defineEmits<{
  close: []
  loadCompany: [domain: string]
}>()

const dragging = ref(false)

const companySections = ref<UserSettingsCompanySection[]>([
  {
    id: 'description',
    opened: true,
    order: 1,
  },
  {
    id: 'main',
    opened: true,
    order: 2,
  },
  {
    id: 'headquarters',
    opened: true,
    order: 3,
  },
  {
    id: 'map',
    opened: true,
    order: 4,
  },
  {
    id: 'companyParent',
    opened: true,
    order: 5,
  },
  {
    id: 'companiesAcquisitions',
    opened: true,
    order: 6,
  },
  {
    id: 'companiesSubsidiaries',
    opened: true,
    order: 7,
  },
  {
    id: 'apps',
    opened: true,
    order: 8,
  },
  {
    id: 'contents',
    opened: true,
    order: 9,
  },
  {
    id: 'contacts',
    opened: true,
    order: 10,
  },
  {
    id: 'technologies',
    opened: true,
    order: 11,
  },
  {
    id: 'linkedin',
    opened: true,
    order: 12,
  },
  {
    id: 'companiesSimilar',
    opened: true,
    order: 13,
  },
])

const { getCompaniesSearchUrl } = useSegmentation()
const { t } = useI18n()
const userStore = useUserStore()

const sortedCompanySections = computed({
  get() {
    return companySections.value.slice().sort((sectionA, sectionB) => {
      return sectionA.order < sectionB.order ? -1 : 0
    })
  },

  async set(newCompanySections) {
    companySections.value = newCompanySections.map((newCompanySection, order) => ({
      ...newCompanySection,
      order: order + 1,
    }))

    await saveCompanySection()
  },
})

function initializeCompanySections() {
  if (userStore.userSettings) {
    const savedSections = userStore.userSettings.companySections || []

    if (Array.isArray(savedSections)) {
      for (const savedSection of savedSections) {
        const companySection = companySections.value.find((companySection) => {
          return companySection.id === savedSection.id
        })

        if (companySection) {
          companySection.opened = savedSection.opened
          companySection.order = savedSection.order
        }
      }
    }
  }
}

async function saveCompanySection() {
  if (userStore.user) {
    await userStore.updateUserSettings({ companySections: companySections.value })
  }
}

function onDragEnd() {
  dragging.value = false
}

function onDragStart() {
  dragging.value = true
}

async function onLoadRelatedCompany(domain: string) {
  emit('loadCompany', domain)
}

async function onToggleCompanySection(sectionId: string) {
  const section = companySections.value.find(companySection => companySection.id === sectionId)

  if (section) {
    section.opened = !section.opened
  }

  await saveCompanySection()
}

initializeCompanySections()
</script>

<template>
  <div
    v-if="company"
    class="group/company-profile-sections size-full"
  >
    <div>
      <Draggable
        v-model="sortedCompanySections"
        v-bind="{
          forceFallback: true,
          ghostClass: 'opacity-0',
          handle: '.js-drag-handle',
          scrollSensitivity: 100,
        }"
        class="flex flex-col"
        item-key="id"
        @end="onDragEnd"
        @start="onDragStart"
      >
        <template #item="{ element }">
          <div>
            <CompanyProfileSection
              v-if="element.id === 'apps' && Object.keys(company?.apps || {}).length"
              :id="element.id"
              :key="`${element.id}_apps`"
              :bordered="bordered"
              :dragging="dragging"
              :opened="element.opened"
              :title="t('datapoints.attributes.apps')"
              @toggle="onToggleCompanySection"
            >
              <CompanyProfileSectionDetail
                v-if="company.apps?.amazon?.[0]?.url"
                :href="company.apps.amazon[0].url"
                :icon="getIcon('amazon')"
                :label="t('datapoints.attributes.apps-amazon')"
                :value="company.apps.amazon[0].name || company.apps.amazon[0].id || company.apps.amazon[0].url"
              />

              <CompanyProfileSectionDetail
                v-if="company.apps?.android?.[0]?.url"
                :href="company.apps.android[0].url"
                :icon="getIcon('android')"
                :label="t('datapoints.attributes.apps-android')"
                :value="company.apps.android[0].name || company.apps.android[0].id || company.apps.android[0].url"
              />

              <CompanyProfileSectionDetail
                v-if="company.apps?.chrome?.[0]?.url"
                :href="company.apps.chrome[0].url"
                :icon="getIcon('chrome')"
                :label="t('datapoints.attributes.apps-chrome')"
                :value="company.apps.chrome[0].name || company.apps.chrome[0].id || company.apps.chrome[0].url"
              />

              <CompanyProfileSectionDetail
                v-if="company.apps?.ios?.[0]?.url"
                :href="company.apps.ios[0].url"
                :icon="getIcon('ios')"
                :label="t('datapoints.attributes.apps-ios')"
                :value="company.apps.ios[0].name || company.apps.ios[0].id || company.apps.ios[0].url"
              />

              <CompanyProfileSectionDetail
                v-if="company.apps?.mac?.[0]?.url"
                :href="company.apps.mac[0].url"
                :icon="getIcon('mac')"
                :label="t('datapoints.attributes.apps-mac')"
                :value="company.apps.mac[0].name || company.apps.mac[0].id || company.apps.mac[0].url"
              />

              <CompanyProfileSectionDetail
                v-if="company.apps?.meta?.[0]?.url"
                :href="company.apps.meta[0].url"
                :icon="getIcon('meta')"
                :label="t('datapoints.attributes.apps-meta')"
                :value="company.apps.meta[0].name || company.apps.meta[0].id || company.apps.meta[0].url"
              />

              <CompanyProfileSectionDetail
                v-if="company.apps?.microsoft?.[0]?.url"
                :href="company.apps.microsoft[0].url"
                :icon="getIcon('microsoft')"
                :label="t('datapoints.attributes.apps-microsoft')"
                :value="company.apps.microsoft[0].name || company.apps.microsoft[0].id || company.apps.microsoft[0].url"
              />

              <CompanyProfileSectionDetail
                v-if="company.apps?.playstation?.[0]?.url"
                :href="company.apps.playstation[0].url"
                :icon="getIcon('playstation')"
                :label="t('datapoints.attributes.apps-playstation')"
                :value="company.apps.playstation[0].name || company.apps.playstation[0].id || company.apps.playstation[0].url"
              />

              <CompanyProfileSectionDetail
                v-if="company.apps?.xbox?.[0]?.url"
                :href="company.apps.xbox[0].url"
                :icon="getIcon('xbox')"
                :label="t('datapoints.attributes.apps-xbox')"
                :value="company.apps.xbox[0].name || company.apps.xbox[0].id || company.apps.xbox[0].url"
              />
            </CompanyProfileSection>

            <CompanyProfileSection
              v-else-if="element.id === 'companiesAcquisitions' && company.companies?.acquisitions?.length"
              :id="element.id"
              :key="`${element.id}_companiesAcquisitions`"
              :bordered="bordered"
              :dragging="dragging"
              :opened="element.opened"
              :title="`${t('datapoints.attributes.companies-acquisitions')} (${company.companies.acquisitions.length})`"
              @toggle="onToggleCompanySection"
            >
              <CompaniesGrid
                :companies="company.companies.acquisitions"
                @company-clicked="onLoadRelatedCompany"
              />
            </CompanyProfileSection>

            <CompanyProfileSection
              v-else-if="element.id === 'companiesSimilar' && similar?.length"
              :id="element.id"
              :key="`${element.id}_companiesSimilar`"
              :bordered="bordered"
              :dragging="dragging"
              :opened="element.opened"
              :title="`${t('globals.similarCompanies')} (${similar.length})`"
              @toggle="onToggleCompanySection"
            >
              <CompaniesGrid
                :companies="similar"
                :max="8"
                @company-clicked="onLoadRelatedCompany"
              />
            </CompanyProfileSection>

            <CompanyProfileSection
              v-else-if="element.id === 'companiesSubsidiaries' && company.companies?.subsidiaries?.length"
              :id="element.id"
              :key="`${element.id}_companiesSubsidiaries`"
              :bordered="bordered"
              :dragging="dragging"
              :opened="element.opened"
              :title="`${t('datapoints.attributes.companies-subsidiaries')} (${company.companies.subsidiaries.length})`"
              @toggle="onToggleCompanySection"
            >
              <CompaniesGrid
                :companies="company.companies.subsidiaries"
                @company-clicked="onLoadRelatedCompany"
              />
            </CompanyProfileSection>

            <CompanyProfileSection
              v-else-if="element.id === 'companyParent' && company.companies?.parent"
              :id="element.id"
              :key="`${element.id}_companyParent`"
              :bordered="bordered"
              :dragging="dragging"
              :opened="element.opened"
              :title="t('datapoints.attributes.companies-parent')"
              @toggle="onToggleCompanySection"
            >
              <CompanyCard
                :company="company.companies?.parent"
                is-clickable
                @avatar-clicked="onLoadRelatedCompany"
              />
            </CompanyProfileSection>

            <CompanyProfileSection
              v-else-if="element.id === 'contacts' && Object.keys(company?.contacts || {}).length"
              :id="element.id"
              :key="`${element.id}_contacts`"
              :bordered="bordered"
              :dragging="dragging"
              :opened="element.opened"
              :title="t('datapoints.attributes.contacts')"
              @toggle="onToggleCompanySection"
            >
              <CompanyProfileSectionDetail
                v-if="company.urls?.contact"
                :href="company.urls.contact"
                :icon="getIcon('contactUrl')"
                :label="t('datapoints.attributes.urls-contact')"
                :value="company.urls.contact"
              />

              <CompanyProfileSectionDetail
                v-if="company.contacts?.emails?.[0].value"
                :href="`mailto:${company.contacts.emails[0].value}`"
                :icon="getIcon('emails')"
                :label="t('datapoints.attributes.contacts-emails')"
                :value="company.contacts.emails[0].value"
              />

              <CompanyProfileSectionDetail
                v-if="company.contacts?.lines?.[0].value"
                :icon="getIcon('lines')"
                :label="t('datapoints.attributes.contacts-lines')"
                :value="company.contacts.lines[0].value"
              />

              <CompanyProfileSectionDetail
                v-if="company.contacts?.phones?.[0].value"
                :href="`tel:${company.contacts.phones[0].value}`"
                :icon="getIcon('phones')"
                :label="t('datapoints.attributes.contacts-phones')"
                :value="company.contacts.phones[0].value"
              />

              <CompanyProfileSectionDetail
                v-if="company.contacts?.whatsapps?.[0].value"
                :href="company.contacts.whatsapps[0].value"
                :icon="getIcon('whatsapps')"
                :label="t('datapoints.attributes.contacts-whatsapps')"
                :value="company.contacts.whatsapps[0].value"
              />
            </CompanyProfileSection>

            <CompanyProfileSection
              v-else-if="element.id === 'contents' && Object.keys(company?.contents || {}).length"
              :id="element.id"
              :key="`${element.id}_contents`"
              :bordered="bordered"
              :dragging="dragging"
              :opened="element.opened"
              :title="t('datapoints.attributes.contents')"
              @toggle="onToggleCompanySection"
            >
              <CompanyProfileSectionDetail
                v-if="company.urls?.blog"
                :href="company.urls.blog"
                :icon="getIcon('blogUrl')"
                :label="t('datapoints.attributes.urls-blog')"
                :value="company.urls.blog"
              />

              <CompanyProfileSectionDetail
                v-if="company.urls?.developers"
                :href="company.urls.developers"
                :icon="getIcon('developersUrl')"
                :label="t('datapoints.attributes.urls-developers')"
                :value="company.urls.developers"
              />

              <CompanyProfileSectionDetail
                v-if="company.urls?.docs"
                :href="company.urls.docs"
                :icon="getIcon('docsUrl')"
                :label="t('datapoints.attributes.urls-docs')"
                :value="company.urls.docs"
              />

              <CompanyProfileSectionDetail
                v-if="company.urls?.events"
                :href="company.urls.events"
                :icon="getIcon('eventsUrl')"
                :label="t('datapoints.attributes.urls-events')"
                :value="company.urls.events"
              />

              <CompanyProfileSectionDetail
                v-if="company.urls?.partnership"
                :href="company.urls.partnership"
                :icon="getIcon('partnershipUrl')"
                :label="t('datapoints.attributes.urls-partnership')"
                :value="company.urls.partnership"
              />

              <CompanyProfileSectionDetail
                v-if="company.urls?.sitemap"
                :href="company.urls.sitemap"
                :icon="getIcon('sitemapUrl')"
                :label="t('datapoints.attributes.urls-sitemap')"
                :value="company.urls.sitemap"
              />

              <CompanyProfileSectionDetail
                v-if="company.urls?.status"
                :href="company.urls.status"
                :icon="getIcon('statusUrl')"
                :label="t('datapoints.attributes.urls-status')"
                :value="company.urls.status"
              />

              <CompanyProfileSectionDetail
                v-if="company.urls?.updates"
                :href="company.urls.updates"
                :icon="getIcon('updatesUrl')"
                :label="t('datapoints.attributes.urls-updates')"
                :value="company.urls.updates"
              />
            </CompanyProfileSection>

            <CompanyProfileSection
              v-else-if="element.id === 'description' && (company?.descriptions?.primary || company?.descriptions?.website || company?.descriptions?.knowledgeGraph || company?.descriptions?.tagline)"
              :id="element.id"
              :key="`${element.id}_description`"
              :bordered="bordered"
              :dragging="dragging"
              :opened="element.opened"
              :title="t('datapoints.attributes.descriptions')"
              @toggle="onToggleCompanySection"
            >
              <BaseTruncated
                class="text-sm"
                :content="company?.descriptions?.primary || company?.descriptions?.website || company?.descriptions?.knowledgeGraph || company?.descriptions?.tagline"
                :max-characters="250"
              />
            </CompanyProfileSection>

            <CompanyProfileSection
              v-else-if="element.id === 'headquarters' && company?.locations?.headquarters"
              :id="element.id"
              :key="`${element.id}_headquarters`"
              :bordered="bordered"
              :dragging="dragging"
              :opened="element.opened"
              :title="t('datapoints.attributes.locations-headquarters')"
              @toggle="onToggleCompanySection"
            >
              <CompanyProfileSectionDetail
                v-if="company.locations.headquarters.address?.raw"
                :icon="getIcon('address')"
                :label="t('datapoints.attributes.locations-headquarters-address')"
                :value="company.locations.headquarters.address.raw"
              />

              <CompanyProfileSectionDetail
                v-if="company.locations.headquarters.city?.name"
                :icon="getIcon('city')"
                :label="t('datapoints.attributes.locations-headquarters-city')"
                :value="company.locations.headquarters.city.name"
              >
                <template
                  v-if="company.locations.headquarters.city.code"
                  #right
                >
                  <CompanyProfileSectionDetailSearch
                    :to="getCompaniesSearchUrl({
                      attribute: 'locations.headquarters.city.code',
                      value: company.locations.headquarters.city.code,
                    })"
                  />
                </template>
              </CompanyProfileSectionDetail>

              <CompanyProfileSectionDetail
                v-if="company.locations.headquarters.county?.name"
                :icon="getIcon('county')"
                :label="t('datapoints.attributes.locations-headquarters-county')"
                :value="company.locations.headquarters.county.name"
              >
                <template
                  v-if="company.locations.headquarters.county.code"
                  #right
                >
                  <CompanyProfileSectionDetailSearch
                    :to="getCompaniesSearchUrl({
                      attribute: 'locations.headquarters.county.code',
                      value: company.locations.headquarters.county.code,
                    })"
                  />
                </template>
              </CompanyProfileSectionDetail>

              <CompanyProfileSectionDetail
                v-if="company.locations.headquarters.state?.name"
                :icon="getIcon('state')"
                :label="t('datapoints.attributes.locations-headquarters-state')"
                :value="company.locations.headquarters.state.name"
              >
                <template
                  v-if="company.locations.headquarters.state?.code"
                  #right
                >
                  <CompanyProfileSectionDetailSearch
                    :to="getCompaniesSearchUrl({
                      attribute: 'locations.headquarters.state.code',
                      value: company.locations.headquarters.state.code,
                    })"
                  />
                </template>
              </CompanyProfileSectionDetail>

              <CompanyProfileSectionDetail
                v-if="company.locations.headquarters.country?.code"
                :icon="getIcon('country')"
                :label="t('datapoints.attributes.locations-headquarters-country')"
              >
                <template #right>
                  <div class="flex items-center">
                    <UIcon
                      class="size-4.5 mr-2"
                      :name="`flagpack:${company.locations.headquarters.country.code}`"
                    />

                    {{ t(`locations.countries.${company.locations.headquarters.country.code}`) }}

                    <CompanyProfileSectionDetailSearch
                      :to="getCompaniesSearchUrl({
                        attribute: 'locations.headquarters.country.code',
                        value: company.locations.headquarters.country.code,
                      })"
                    />
                  </div>
                </template>
              </CompanyProfileSectionDetail>

              <CompanyProfileSectionDetail
                v-if="company.locations.headquarters.continent?.code"
                :icon="getIcon('continent')"
                :label="t('datapoints.attributes.locations-headquarters-continent')"
              >
                <template #right>
                  <div class="flex items-center">
                    {{ t(`locations.continents.${company.locations.headquarters.continent.code}`) }}

                    <CompanyProfileSectionDetailSearch
                      :to="getCompaniesSearchUrl({
                        attribute: 'locations.headquarters.continent.code',
                        value: company.locations.headquarters.continent.code,
                      })"
                    />
                  </div>
                </template>
              </CompanyProfileSectionDetail>
            </CompanyProfileSection>

            <CompanyProfileSection
              v-else-if="element.id === 'main' && (company?.about?.industries?.length || (company?.codes?.naics || company?.codes?.sic) || company?.analytics?.monthlyVisitors)"
              :id="element.id"
              :key="`${element.id}_main`"
              :bordered="bordered"
              :dragging="dragging"
              :opened="element.opened"
              :title="t('mainInformation')"
              @toggle="onToggleCompanySection"
            >
              <CompanyProfileSectionDetail
                v-if="company.about?.industries?.length"
                :icon="getIcon('industries')"
                :label="t('datapoints.attributes.about-industries')"
              >
                <template #left>
                  <CompanyIndustries
                    :company="company"
                    :max="10"
                    size="xs"
                  />
                </template>
              </CompanyProfileSectionDetail>

              <CompanyProfileSectionDetail
                v-if="company.codes?.naics"
                :icon="getIcon('industryNaics')"
                :label="t('datapoints.attributes.codes-naics')"
                :value="company.codes.naics"
              />

              <CompanyProfileSectionDetail
                v-if="company.codes?.sic"
                :icon="getIcon('industrySic')"
                :label="t('datapoints.attributes.codes-sic')"
                :value="company.codes.sic"
              />

              <CompanyProfileSectionDetail
                v-if="company.analytics?.monthlyVisitors"
                :icon="getIcon('monthlyVisitors')"
                :label="t('datapoints.attributes.analytics-monthlyVisitors')"
              >
                <template #left>
                  <div class="flex items-center">
                    <BaseCircle
                      class="mr-2 size-4"
                      :color="getColorForAttribute('monthlyVisitors', company.analytics.monthlyVisitors)"
                      gradient
                    />

                    {{ t(`ranges.monthlyVisitors.${company.analytics.monthlyVisitors}`) }}
                  </div>
                </template>

                <template #right>
                  <CompanyProfileSectionDetailSearch
                    :to="getCompaniesSearchUrl({
                      attribute: 'analytics.monthlyVisitors',
                      value: company.analytics.monthlyVisitors,
                    })"
                  />
                </template>
              </CompanyProfileSectionDetail>

              <CompanyProfileSectionDetail
                v-if="company.about?.businessType"
                :icon="getIcon('businessType')"
                :label="t('datapoints.attributes.about-businessType')"
                :value="t(`ranges.businessType.${company?.about?.businessType}`)"
              />

              <CompanyProfileSectionDetail
                v-if="company.finances?.revenue"
                :icon="getIcon('revenue')"
                :label="t('datapoints.attributes.finances-revenue')"
                :value="t(`ranges.revenue.${company.finances.revenue}`)"
              >
                <template #right>
                  <CompanyProfileSectionDetailSearch
                    :to="getCompaniesSearchUrl({
                      attribute: 'finances.revenue',
                      value: company.finances.revenue,
                    })"
                  />
                </template>
              </CompanyProfileSectionDetail>

              <CompanyProfileSectionDetail
                v-if="company.finances?.stockExchange"
                :icon="getIcon('stockExchanges')"
                :label="t('datapoints.attributes.finances-stockExchange')"
                :value="company.finances.stockExchange"
              >
                <template #right>
                  <CompanyProfileSectionDetailSearch
                    :to="getCompaniesSearchUrl({
                      attribute: 'finances.stockExchange',
                      value: company.finances.stockExchange,
                    })"
                  />
                </template>
              </CompanyProfileSectionDetail>

              <CompanyProfileSectionDetail
                v-if="company.finances?.stockSymbol"
                :icon="getIcon('stockSymbol')"
                :label="t('datapoints.attributes.finances-stockSymbol')"
                :value="company.finances.stockSymbol"
              />

              <CompanyProfileSectionDetail
                v-if="company.about?.totalEmployees"
                :icon="getIcon('totalEmployees')"
                :label="t('datapoints.attributes.about-totalEmployees')"
                :value="t(`ranges.totalEmployees.${company.about.totalEmployees}`)"
              />

              <CompanyProfileSectionDetail
                v-if="company.about?.yearFounded"
                :icon="getIcon('yearFounded')"
                :label="t('datapoints.attributes.about-yearFounded')"
                :value="company.about.yearFounded"
              />
            </CompanyProfileSection>

            <CompanyProfileSection
              v-else-if="element.id === 'map' && getGeopoint(company)?.lat && getGeopoint(company)?.lon"
              :id="element.id"
              :key="`${element.id}_map`"
              :bordered="bordered"
              :dragging="dragging"
              :opened="element.opened"
              :title="t('map.title')"
              @toggle="onToggleCompanySection"
            >
              <NuxtErrorBoundary>
                <WidgetMap
                  :city="company.locations?.headquarters?.city?.name"
                  class="h-52"
                  :country-code="company.locations?.headquarters?.country?.code"
                  :latitude="getGeopoint(company)?.lat"
                  :longitude="getGeopoint(company)?.lon"
                  :state="company?.locations?.headquarters?.state?.name"
                  :type="company?.locations?.headquarters?.city ? 'city' : company?.locations?.headquarters?.country ? 'country' : undefined"
                />

                <template #error="{ clearError }">
                  <div class="flex w-full items-center justify-center">
                    <UButton
                      :label="t('map.error')"
                      variant="link"
                      @click="clearError"
                    />
                  </div>
                </template>
              </NuxtErrorBoundary>
            </CompanyProfileSection>

            <CompanyProfileSection
              v-else-if="element.id === 'technologies' && company.technologies?.active.length"
              :id="element.id"
              :key="`${element.id}_technologies`"
              :bordered="bordered"
              :dragging="dragging"
              :opened="element.opened"
              :title="t('datapoints.attributes.technologies')"
              @toggle="onToggleCompanySection"
            >
              <CompanyTechnologies
                size="2xl"
                :technologies="company.technologies.active"
                :technologies-details="company.technologies.details"
              />
            </CompanyProfileSection>

            <CompanyProfileSection
              v-else-if="element.id === 'linkedin' && company?.socials?.linkedin?.url"
              :id="element.id"
              :key="`${element.id}_linkedin`"
              :bordered="bordered"
              :dragging="dragging"
              :opened="element.opened"
              :title="t('datapoints.attributes.socials-linkedin')"
              @toggle="onToggleCompanySection"
            >
              <div class="flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-4">
                <UTooltip
                  v-if="company.socials.linkedin.url"
                  class="flex-1"
                  :text="t('linkedin.company.tooltip')"
                >
                  <UButton
                    block
                    class="flex justify-center ring-1 ring-gray-200 transition hover:no-underline hover:ring-gray-300 dark:ring-gray-800 hover:dark:ring-gray-700"
                    color="black"
                    :icon="getIcon('linkedin')"
                    :label="t('linkedin.company.label')"
                    target="_blank"
                    :to="company.socials.linkedin.url"
                    variant="link"
                  />
                </UTooltip>

                <UTooltip
                  v-if="company.socials.linkedin.url"
                  class="flex-1"
                  :text="t('linkedin.employees.tooltip')"
                >
                  <UButton
                    block
                    class="flex justify-center ring-1 ring-gray-200 transition hover:no-underline hover:ring-gray-300 dark:ring-gray-800 hover:dark:ring-gray-700"
                    color="black"
                    :icon="getIcon('totalEmployees')"
                    :label="t('linkedin.employees.label')"
                    target="_blank"
                    :to="`${company.socials.linkedin.url.replace(/\/$/, '')}/people/`"
                    variant="link"
                  />
                </UTooltip>

                <UTooltip
                  v-if="company.urls?.salesNavigator"
                  class="flex-1"
                  :text="t('linkedin.salesNavigator.tooltip')"
                >
                  <UButton
                    block
                    class="flex justify-center ring-1 ring-gray-200 transition hover:no-underline hover:ring-gray-300 dark:ring-gray-800 hover:dark:ring-gray-700"
                    color="black"
                    :icon="getIcon('linkedinSalesNavigator')"
                    :label="t('linkedin.salesNavigator.label')"
                    target="_blank"
                    :to="company.urls.salesNavigator"
                    variant="link"
                  />
                </UTooltip>
              </div>
            </CompanyProfileSection>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "linkedin": {
      "company": {
        "label": "LinkedIn",
        "tooltip": "Show LinkedIn profile"
      },
      "employees": {
        "label": "Employees",
        "tooltip": "Show employees on LinkedIn"
      },
      "salesNavigator": {
        "label": "Sales Navigator",
        "tooltip": "Show Sales Navigator Profile"
      }
    },
    "mainInformation": "Main information",
    "map": {
      "error": "Error loading map, click to retry",
      "title": "Locations"
    }
  },
  "es": {
    "linkedin": {
      "company": {
        "label": "LinkedIn",
        "tooltip": "Mostrar perfil de LinkedIn"
      },
      "employees": {
        "label": "Empleados",
        "tooltip": "Mostrar empleados en LinkedIn"
      },
      "salesNavigator": {
        "label": "Sales Navigator",
        "tooltip": "Mostrar perfil de Sales Navigator"
      }
    },
    "mainInformation": "Información principal",
    "map": {
      "error": "Error al cargar el mapa, haga clic para reintentar",
      "title": "Ubicaciones"
    }
  },
  "fr": {
    "linkedin": {
      "company": {
        "label": "LinkedIn",
        "tooltip": "Voir le profil LinkedIn"
      },
      "employees": {
        "label": "Employés",
        "tooltip": "Voir les employés sur LinkedIn"
      },
      "salesNavigator": {
        "label": "Sales Navigator",
        "tooltip": "Voir le profil Sales Navigator"
      }
    },
    "mainInformation": "Informations principales",
    "map": {
      "error": "Erreur lors du chargement de la carte, cliquez pour réessayer",
      "title": "Localisations"
    }
  }
}
</i18n>
